/**
 * @zendir/ui - Spacer Layout Primitive
 *
 * Flexible space filler for Flex and Stack layouts.
 * Expands to fill available space, pushing siblings apart.
 *
 * @example
 * ```tsx
 * <Flex align="center" gap="sm">
 *   <H3>Telemetry</H3>
 *   <Spacer />
 *   <Button variant="secondary" size="small">Refresh</Button>
 *   <Button size="small">Export</Button>
 * </Flex>
 * ```
 */

import React, { memo } from "react";

export interface SpacerProps {
  /** Fixed size instead of flexible (spacing token compatible, number → px) */
  size?: string | number;
}

export const Spacer = memo(function Spacer({
  size,
}: SpacerProps): React.ReactElement {
  const resolvedSize = typeof size === "number" ? `${size}px` : size;

  if (resolvedSize) {
    return (
      <div
        aria-hidden="true"
        style={{
          flexShrink: 0,
          width: resolvedSize,
          height: resolvedSize,
        }}
      />
    );
  }

  return (
    <div
      aria-hidden="true"
      style={{
        flex: "1 1 0%",
      }}
    />
  );
});

export default Spacer;
